/**
 * MAHI — client-side photo downscale, before it ever hits the network.
 *
 * A raw phone photo (3-8 MB is routine on a modern iPhone/Android camera)
 * can trip a hard AWS limit this app can't configure around: API Gateway's
 * HTTP API proxies every request into a synchronous Lambda invocation, and
 * Lambda's own payload ceiling for that is a fixed 6 MB — inflated further
 * by ~33% because the binary body travels to Lambda base64-encoded. Above
 * roughly 4-4.5 MB raw, API Gateway rejects the upload outright with 413
 * before it ever reaches our code, which is what a photo taken straight
 * from a modern phone's camera routinely does on the very first try.
 *
 * MAX_PX / JPEG_QUALITY below MUST stay in step with
 * PROFILE_PHOTO_MAX_PX / PROFILE_PHOTO_JPEG_QUALITY in
 * services/mira/photos.py (MAHI_PHOTO_MAX_PX / MAHI_PHOTO_JPEG_QUALITY
 * override them in staging/prod). That is what keeps the photo to a single lossy
 * re-encode: the server passes through anything already inside its pixel and
 * byte budgets, so a file this function produces is stored as-is rather than
 * being encoded a second time. Drift between the two files silently
 * reintroduces the double encode — and with it the soft, over-smoothed
 * try-on results that motivated raising these numbers in the first place.
 *
 * Only worth doing above the threshold that actually risks the 413 — most
 * gallery photos (screenshots, anything already shared/downloaded once)
 * are already comfortably under it, and re-encoding those would just cost
 * quality for nothing. Photos under this threshold are sent untouched and
 * the server does the single re-encode instead.
 */
const MAX_PX = 2048;
const JPEG_QUALITY = 0.9;
const SKIP_BELOW_BYTES = 4 * 1024 * 1024;

/**
 * Returns a downscaled JPEG File, or the original File unchanged if it's
 * already under the size that risks a 413, or if the browser can't decode
 * it (rare format) or is too old to support the APIs used here — fails
 * open rather than blocking the upload.
 */
export default async function compressPhoto(file) {
  if (!file || !file.type?.startsWith('image/')) return file;
  if (file.size <= SKIP_BELOW_BYTES) return file;
  try {
    // `imageOrientation: 'from-image'` applies the photo's own EXIF rotation
    // during decode — without it, re-encoding a portrait phone photo through
    // a canvas silently turns it 90 degrees.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, MAX_PX / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
    if (!blob) return file;

    const name = (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}
